// Equipment: boss drops that persist across ascension.
import type { AddStat, MultStat } from '../game/stats';

export type Slot = 'weapon' | 'helmet' | 'armor' | 'aura' | 'charm';
export type Rarity = 'common' | 'rare' | 'epic' | 'legendary' | 'mythic';
export type SetId = 'dragon' | 'shadow' | 'titan' | 'arcane';

export interface SlotDef { id: Slot; name: string; icon: string; stat: MultStat; statLabel: string }

export const SLOTS: readonly SlotDef[] = [
  { id: 'weapon', name: 'Sword', icon: '🗡️', stat: 'tapDamage',  statLabel: 'Tap damage' },
  { id: 'helmet', name: 'Helmet', icon: '⛑️', stat: 'heroDamage', statLabel: 'Guardian damage' },
  { id: 'armor',  name: 'Armor', icon: '🛡️', stat: 'bossDamage', statLabel: 'Giant damage' },
  { id: 'aura',   name: 'Cape', icon: '🧣', stat: 'allDamage',  statLabel: 'All damage' },
  { id: 'charm',  name: 'Lucky Charm', icon: '🧿', stat: 'gold',       statLabel: 'Coins' },
];

export interface RarityDef { id: Rarity; name: string; color: string; power: number; weight: number }

/** power scales the item's stat; weight is the relative drop chance. */
export const RARITIES: readonly RarityDef[] = [
  { id: 'common',    name: 'Common',    color: '#b8b3cc', power: 0.5, weight: 60 },
  { id: 'rare',      name: 'Rare',      color: '#58a6ff', power: 1,   weight: 26 },
  { id: 'epic',      name: 'Epic',      color: '#b77bff', power: 2,   weight: 10 },
  { id: 'legendary', name: 'Legendary', color: '#ffb02e', power: 4,   weight: 3.5 },
  { id: 'mythic',    name: 'Mythic',    color: '#ff4d6d', power: 8,   weight: 0.5 },
];

export type SetBonus =
  | { kind: 'mult'; stat: MultStat; value: number }
  | { kind: 'add'; stat: AddStat; value: number };

export interface SetDef {
  id: SetId;
  name: string;
  /** Bonuses at 3 and 5 pieces equipped (5 includes the 3-piece bonus too). */
  three: readonly SetBonus[];
  five: readonly SetBonus[];
  desc3: string;
  desc5: string;
}

export const SETS: readonly SetDef[] = [
  { id: 'dragon', name: 'Dino Rider',
    three: [{ kind: 'mult', stat: 'allDamage', value: 1.5 }], desc3: 'All damage +50%',
    five: [{ kind: 'add', stat: 'fireswordBonus', value: 3 }, { kind: 'mult', stat: 'tapDamage', value: 3 }], desc5: 'Slash +x3, tap damage x3' },
  { id: 'shadow', name: 'Ninja Shadow',
    three: [{ kind: 'add', stat: 'critChance', value: 0.05 }], desc3: 'Crit chance +5%',
    five: [{ kind: 'mult', stat: 'critDamage', value: 4 }], desc5: 'Crit damage x4' },
  { id: 'titan', name: 'Tin Soldier',
    three: [{ kind: 'mult', stat: 'bossDamage', value: 1.75 }], desc3: 'Giant damage +75%',
    five: [{ kind: 'add', stat: 'bossTimeSec', value: 10 }, { kind: 'mult', stat: 'heroDamage', value: 3 }], desc5: 'Giant timer +10s, Guardian damage x3' },
  { id: 'arcane', name: 'Space Ranger',
    three: [{ kind: 'add', stat: 'manaRegenPct', value: 0.5 }], desc3: 'Energy regeneration +50%',
    five: [{ kind: 'add', stat: 'maxMana', value: 100 }, { kind: 'mult', stat: 'allDamage', value: 2 }], desc5: 'Max energy +100, all damage x2' },
];

export const EQUIPMENT_TUNING = {
  /** Chance for a boss kill to drop an item. */
  bossDropChance: 0.08,
  /** First clear of every Nth stage guarantees a drop. */
  guaranteedEvery: 25,
  /** Chance a drop belongs to a set (otherwise it's a plain item). */
  setChance: 0.3,
  /** Minimum stage for set items to drop. */
  setMinStage: 50,
  inventoryCap: 40,
  /** Item stat = 1 + rarityPower * (stage ^ stageExp) / stageDiv. */
  stageExp: 0.5,
  stageDiv: 20,
} as const;
